// src/AdminDashboard.js
// Aggregated usage dashboard. Gated by ADMIN_SECRET; key cached in sessionStorage.
import React, { useEffect, useMemo, useState } from 'react';
import './AdminDashboard.css';

const SECRET_KEY = 'phutball_admin_key';

function getBaseUrl() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const isProduction = window.location.protocol === 'https:';
  return isProduction
    ? `${window.location.protocol}//${window.location.host}`
    : `http://${window.location.hostname}:8000`;
}

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const fmtUsd = (n) => (n == null ? '—' : `$${Number(n).toFixed(4)}`);
const fmtMs = (ms) => {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const AdminDashboard = ({ onExit }) => {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(SECRET_KEY) || '');
  const [keyInput, setKeyInput] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasKey = Boolean(adminKey);

  useEffect(() => {
    if (!hasKey) return;
    setLoading(true);
    setError(null);
    fetch(`${getBaseUrl()}/admin/telemetry/summary?key=${encodeURIComponent(adminKey)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setData)
      .catch((err) => {
        setError(err.message);
        sessionStorage.removeItem(SECRET_KEY);
        setAdminKey('');
      })
      .finally(() => setLoading(false));
  }, [adminKey, hasKey]);

  const submitKey = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    sessionStorage.setItem(SECRET_KEY, keyInput.trim());
    setAdminKey(keyInput.trim());
  };

  const signOut = () => {
    sessionStorage.removeItem(SECRET_KEY);
    setAdminKey('');
    setData(null);
  };

  const dailyChart = useMemo(() => {
    if (!data?.dailyCounts?.length) return null;
    const days = [...new Set(data.dailyCounts.map((r) => r.day))].sort();
    const byDay = new Map();
    for (const r of data.dailyCounts) {
      if (!byDay.has(r.day)) byDay.set(r.day, {});
      byDay.get(r.day)[r.event_type] = r.count;
    }
    const maxCount = Math.max(
      1,
      ...data.dailyCounts.map((r) => r.count)
    );
    return { days, byDay, maxCount };
  }, [data]);

  if (!hasKey) {
    return (
      <div className="admin-dashboard">
        <div className="admin-login">
          <h1>Admin Dashboard</h1>
          <p className="admin-subtitle">Enter admin key to view telemetry</p>
          <form onSubmit={submitKey}>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key"
              autoFocus
            />
            <button type="submit">Enter</button>
          </form>
          {error && <p className="admin-error">{error}</p>}
          <button className="admin-back" onClick={onExit}>← Back to game</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-empty">Loading…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="admin-dashboard">
        <div className="admin-empty">No data yet.</div>
      </div>
    );
  }

  const totalMenuEvents = data.modeCounts.reduce((s, r) => s + (r.count || 0), 0);
  const totalGames = data.gameOutcomes.reduce((s, r) => s + (r.count || 0), 0);
  const totalTokenCost = data.tokenTotals.reduce((s, r) => s + (r.total_cost_usd || 0), 0);

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div>
          <h1>Usage Dashboard</h1>
          <p className="admin-subtitle">Last 30 days</p>
        </div>
        <div className="admin-actions">
          <button onClick={signOut}>Sign out</button>
          <button onClick={onExit}>← Back</button>
        </div>
      </header>

      <section className="admin-grid">
        <div className="admin-card admin-stat">
          <div className="admin-stat-label">Menu events</div>
          <div className="admin-stat-value">{fmt(totalMenuEvents)}</div>
        </div>
        <div className="admin-card admin-stat">
          <div className="admin-stat-label">Games completed</div>
          <div className="admin-stat-value">{fmt(totalGames)}</div>
        </div>
        <div className="admin-card admin-stat">
          <div className="admin-stat-label">Total LLM cost</div>
          <div className="admin-stat-value">{fmtUsd(totalTokenCost)}</div>
        </div>
      </section>

      <section className="admin-card">
        <h2>Mode breakdown</h2>
        {data.modeCounts.length === 0 ? (
          <p className="admin-empty">No menu events yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Mode</th><th>Count</th><th>%</th></tr>
            </thead>
            <tbody>
              {data.modeCounts.map((row) => (
                <tr key={row.mode || 'unknown'}>
                  <td>{row.mode || '(unknown)'}</td>
                  <td>{fmt(row.count)}</td>
                  <td>{totalMenuEvents ? ((row.count / totalMenuEvents) * 100).toFixed(1) : '—'}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-card">
        <h2>Tutorial funnel</h2>
        {data.tutorialFunnel.length === 0 ? (
          <p className="admin-empty">No tutorial data yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Type</th><th>Starts</th><th>Completions</th><th>Completion rate</th></tr>
            </thead>
            <tbody>
              {data.tutorialFunnel.map((row) => {
                const rate = row.starts > 0 ? (row.completions / row.starts) * 100 : 0;
                return (
                  <tr key={row.tutorial_type}>
                    <td>{row.tutorial_type}</td>
                    <td>{fmt(row.starts)}</td>
                    <td>{fmt(row.completions)}</td>
                    <td>{rate.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-card">
        <h2>LLM popularity</h2>
        {data.llmModels.length === 0 ? (
          <p className="admin-empty">No LLM game starts recorded.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Provider</th><th>Model</th><th>Games started</th></tr>
            </thead>
            <tbody>
              {data.llmModels.map((row) => (
                <tr key={`${row.provider}-${row.model}`}>
                  <td>{row.provider}</td>
                  <td>{row.model}</td>
                  <td>{fmt(row.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-card">
        <h2>Token usage per model</h2>
        {data.tokenTotals.length === 0 ? (
          <p className="admin-empty">No completed LLM games yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Games</th>
                <th>Input</th>
                <th>Output</th>
                <th>Reasoning</th>
                <th>Avg cost</th>
                <th>Total cost</th>
              </tr>
            </thead>
            <tbody>
              {data.tokenTotals.map((row) => (
                <tr key={`${row.provider}-${row.model}`}>
                  <td>{row.provider}</td>
                  <td>{row.model}</td>
                  <td>{fmt(row.games)}</td>
                  <td>{fmt(row.total_input_tokens)}</td>
                  <td>{fmt(row.total_output_tokens)}</td>
                  <td>{fmt(row.total_reasoning_tokens)}</td>
                  <td>{fmtUsd(row.avg_cost_usd)}</td>
                  <td>{fmtUsd(row.total_cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-card">
        <h2>Time control popularity</h2>
        {(!data.timeControlCounts || data.timeControlCounts.length === 0) ? (
          <p className="admin-empty">No completed games with time control yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Time control</th>
                <th>Mode</th>
                <th>Games</th>
                <th>Avg turns</th>
                <th>Avg duration</th>
                <th>Total timeouts</th>
                <th>Avg per game</th>
              </tr>
            </thead>
            <tbody>
              {data.timeControlCounts.map((row, i) => (
                <tr key={i}>
                  <td>{row.time_control || '(none)'}</td>
                  <td>{row.mode || '(unknown)'}</td>
                  <td>{fmt(row.count)}</td>
                  <td>{row.avg_turns ? row.avg_turns.toFixed(1) : '—'}</td>
                  <td>{fmtMs(row.avg_duration_ms)}</td>
                  <td>{fmt(row.total_timeouts)}</td>
                  <td>{row.avg_timeouts_per_game != null ? row.avg_timeouts_per_game.toFixed(2) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-card">
        <h2>Game outcomes</h2>
        {data.gameOutcomes.length === 0 ? (
          <p className="admin-empty">No game completions yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Mode</th><th>Winner</th><th>Games</th><th>Avg turns</th><th>Avg duration</th></tr>
            </thead>
            <tbody>
              {data.gameOutcomes.map((row, i) => (
                <tr key={i}>
                  <td>{row.mode || '(unknown)'}</td>
                  <td>{row.winner === '1' || row.winner === 1 ? 'P1' : row.winner === '2' || row.winner === 2 ? 'P2' : '—'}</td>
                  <td>{fmt(row.count)}</td>
                  <td>{row.avg_turns ? row.avg_turns.toFixed(1) : '—'}</td>
                  <td>{fmtMs(row.avg_duration_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {dailyChart && (
        <section className="admin-card">
          <h2>Daily activity</h2>
          <div className="admin-bars">
            {dailyChart.days.map((day) => {
              const events = dailyChart.byDay.get(day) || {};
              const menu = events.menu_event || 0;
              const complete = events.game_complete || 0;
              const tutorial = events.tutorial_progress || 0;
              const max = dailyChart.maxCount;
              return (
                <div key={day} className="admin-bar-row">
                  <div className="admin-bar-label">{day}</div>
                  <div className="admin-bar-track">
                    <div className="admin-bar-seg admin-bar-menu" style={{ width: `${(menu / max) * 100}%` }} title={`menu_event: ${menu}`} />
                    <div className="admin-bar-seg admin-bar-complete" style={{ width: `${(complete / max) * 100}%` }} title={`game_complete: ${complete}`} />
                    <div className="admin-bar-seg admin-bar-tutorial" style={{ width: `${(tutorial / max) * 100}%` }} title={`tutorial_progress: ${tutorial}`} />
                  </div>
                  <div className="admin-bar-count">{menu + complete + tutorial}</div>
                </div>
              );
            })}
          </div>
          <div className="admin-legend">
            <span><i className="admin-bar-menu" /> menu_event</span>
            <span><i className="admin-bar-complete" /> game_complete</span>
            <span><i className="admin-bar-tutorial" /> tutorial_progress</span>
          </div>
        </section>
      )}
    </div>
  );
};

export default AdminDashboard;
